import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { BaseState } from "models/common/baseState";
import { ManagedCatalogueAddUpdate, ManagedCatalogueView } from "models/dbModels/nanagedCatalogues/ManagedCatalogue";
import { ManagedCataloguesSearchFilterQuery } from "models/filters/managedCatalogues/ManagedCataloguesSearchFilterQuery";
import { CatalogueSearchFunction } from "models/functionModels/catalogues/CatalogueSearchFunction";
import { ManagedCataloguesApi } from "apis/ManagedCataloguesApi";
import { UpgradeProcessApi } from "apis/UpgradeProcessApi";


export const getCatalogues = createAsyncThunk("cataloguesTableSlice/getCatalogues", async (params?: ManagedCataloguesSearchFilterQuery) => {
    const newResponse = await ManagedCataloguesApi.Search(params ? { ...params } : {});
    const oldResponse = await ManagedCataloguesApi.SearchOld(params ? { ...params } : {});

    return {
        data: [
            ...oldResponse.data.data.map(i => ({...i, isOld: true})),
            ...newResponse.data.data.map(i => ({...i, isOld: false})), 
        ],
    }
});


export const getOldCatalogueDetails = createAsyncThunk("cataloguesTableSlice/getOldCatalogueDetails", async (params: ManagedCataloguesSearchFilterQuery) => {
    const response = await ManagedCataloguesApi.SearchOld(params);
    return response.data.data[0];
});

export const getCatalogueDetails = createAsyncThunk("cataloguesTableSlice/getCatalogueDetails", async (params: ManagedCataloguesSearchFilterQuery) => {
    const response = await ManagedCataloguesApi.Search(params);
    return response.data.data[0];
});

export const createCatalogue = createAsyncThunk("cataloguesTableSlice/createCatalogue", async (params: ManagedCatalogueAddUpdate[]) => {
    const response = await ManagedCataloguesApi.Add(params);
    return response.data;
});

export const updateCatalogue = createAsyncThunk("cataloguesTableSlice/updateCatalogue", async (params: ManagedCatalogueAddUpdate[]) => {
    const response = await ManagedCataloguesApi.Update(params);
    return response.data;
});

export const deleteCatalogue = createAsyncThunk("cataloguesTableSlice/deleteCatalogue", async (params: ManagedCataloguesSearchFilterQuery) => {
    const response = await ManagedCataloguesApi.Delete(params);
    return response.data;
});

export const getCatalogueStores = createAsyncThunk("cataloguesTableSlice/getCatalogueStores", async (params: CatalogueSearchFunction) => {
    const response = await ManagedCataloguesApi.SearchStores(params);
    return response.data;
});

export const upgradeManagedCatalogues = createAsyncThunk("cataloguesTableSlice/upgradeManagedCatalogues", async () => {
    const response = await UpgradeProcessApi.UpgradeManagedCatalogues();
    return response.data;
});

interface StateProps<T> extends BaseState<T> {
    id: number,
    stores: [],
    sentStoreList: number[],
    allowedStores: [],
    selectManaged: { position: number, id: number }[][],
    selectedRows: { position: number, id: number }[][],
    selectedStocks: { position: number, id: number }[][],
    selectedStores: { position: number, id: number }[][],
    selectedProducts: [],
    loading: boolean,
    loadingStores: boolean,
    loadingStocks: boolean,
    currentStockSelected: [],
    stockPopup: boolean,
    storesPopup: boolean,
    selectedStoresPopup: [],
    managedStockPopup: boolean,
    importWizardPopup: boolean,
    keyword: string,
    totalRows: number,
    totalPages: number,
    pageIndex: number,
    error: string,
    table: ManagedCatalogueView[],
    promptForMigration: boolean,
}

const initialState: StateProps<ManagedCatalogueView> =
{
    id: 0,
    model: {
        id: 0,
        name: "",
        catalogueId: "",
        created: new Date().toISOString(),
        startDate: null,
        endDate: null,
        catalogueStocks: [],
        stores: [],
        pdes: [],
        apns: []
    },
    stores: [],
    sentStoreList: [],
    allowedStores: [],
    selectManaged: [],
    selectedRows: [],
    selectedStocks: [],
    selectedStores: [],
    selectedProducts: [],
    loading: false,
    loadingStores: false,
    loadingStocks: false,
    currentStockSelected: [],
    stockPopup: false,
    storesPopup: false,
    selectedStoresPopup: [],
    managedStockPopup: false,
    importWizardPopup: false,
    keyword: "",
    totalRows: 0,
    totalPages: 0,
    pageIndex: 0,
    error: "",
    table: [],

    promptForMigration: true,
};

type importWizardStateType = {
    importBy: string,
    statusPercentage: number,
    importWizardTableColumns: [],
    importWizardTable: [],
    apnColumn: string,
    pdeColumn: string,
    supplierColumn: string,
    cataloguePriceColumn: string,
    fileSheets: [],
    columnsList: [],
    currentSheet: string,
    fileWorkBook: [],
    fileLoaded: boolean,
    readyToProcess: boolean,
    readyToImport: boolean,
    isProcessing: boolean,
    processedStocks: number,
    totalStocksToImport: number,

}

const importWizardState : importWizardStateType = {
    importBy: "",
    statusPercentage: 10,
    importWizardTableColumns: [],
    importWizardTable: [],
    apnColumn: "",
    pdeColumn: "",
    supplierColumn: "",
    cataloguePriceColumn: "",
    fileSheets: [],
    columnsList: [],
    currentSheet: "",
    fileWorkBook: [],
    fileLoaded: false,
    readyToProcess: false,
    readyToImport: false,
    isProcessing: false,
    processedStocks: 0,
    totalStocksToImport: 0,
};

const cataloguesSlice = createSlice({
    name: "cataloguesSlice",
    initialState: {
        ...initialState,
        ...importWizardState,
    },
    reducers: {
        updateCatalogueField: (state, action) => {
            return {
                ...state,
                [action.payload.key]: action.payload.value,
            };
        },
        updateCatalogueModelField: (state, action) => {
            // @ts-ignore
            state.model[action.payload.key] = action.payload.value;
        },
        addSelectedRows: (state, action) => {
            // @ts-ignore
            state.selectedRows[action.payload.index] = action.payload.rows;
        },
        addSelectedStores: (state, action) => {
            // @ts-ignore
            state.selectedStores = action.payload.rows;
        },

        cleanCatalogueDetails: (state) => {
            return {
                ...state,
                ...initialState,
            };
        },
        resetImportWizardState: (state) => {
            return {
                ...state,
                ...importWizardState,
            };
        },
        addImportWizardTable: (state, action) => {
            // @ts-ignore
            state.importWizardTable = state.importWizardTable.concat(action.payload);
        },
        addAGGridSelectedRows: (state, action) => {
            return {
                ...state,
                selectedRows: action.payload
            };
        },
        addAGGridSelectedStocks: (state, action) => {
            return {
                ...state,
                selectedStocks: action.payload
            };
        },
        addAGGridSelectedStores: (state, action) => {
            return {
                ...state,
                selectedStores: action.payload
            };
        },
        addAGGridSelectedManaged: (state, action) => {
            return {
                ...state,
                selectedManaged: action.payload
            };
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getCatalogues.pending, (state) => {
            state.loading = true;
            state.table = [];
            state.error = "";
        });
        builder.addCase(getCatalogues.fulfilled, (state, action) => {
            state.loading = false;
            state.table = action.payload.data ? action.payload.data : [];
        });
        builder.addCase(getCatalogues.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message || "";
        });
        builder.addCase(getCatalogueDetails.fulfilled, (state, action) => {
            state.model.id = action.payload.id;
            state.model.catalogueId = action.payload.catalogueId;
            state.model.name = action.payload.name;
            state.model.startDate = action.payload.startDate;
            state.model.endDate = action.payload.endDate;
            state.model.catalogueStocks = action.payload.catalogueStocks || [];
            state.model.apns = action.payload.apns || [];
            state.model.pdes = action.payload.pdes || [];
            state.model.stores = action.payload.stores;
            state.sentStoreList = action.payload.stores;
        });
        builder.addCase(getCatalogueDetails.pending, (state) => {
            state.loadingStocks = true;
            state.model.catalogueStocks = [];
            state.error = "";
        });
        builder.addCase(getCatalogueDetails.rejected, (state, action) => {
            state.loadingStocks = false;
            state.error = action.error.message || "";
        });
        builder.addCase(deleteCatalogue.fulfilled, (state, action) => {
            state.selectedRows = [];
        });
        builder.addCase(getCatalogueStores.fulfilled, (state, action) => {
            let storesList: any[] = [];
            action.payload.stores.forEach((store: any) => {
                // @ts-ignore
                if (state.model.stores.includes(store.storeId)) storesList.push({
                    name: store.storeName,
                    storeId: store.storeId,
                    id: store.storeId,
                    lines: store.response ? (store.response.data[0] ? store.response.data[0].lines.length : false) : false
                });
            });
            // @ts-ignore
            state.stores = storesList;
            state.loadingStores = false;
        });
        builder.addCase(getCatalogueStores.pending, (state) => {
            state.loadingStores = true;
            state.stores = [];
            state.error = "";
        });





        builder.addCase(getOldCatalogueDetails.fulfilled, (state, action) => {
            state.model.id = action.payload.id;
            state.model.catalogueId = action.payload.catalogueId;
            state.model.name = action.payload.name;
            state.model.startDate = action.payload.startDate;
            state.model.endDate = action.payload.endDate;
            state.model.catalogueStocks = action.payload.catalogueStocks || [];
            state.model.apns = action.payload.apns || [];
            state.model.pdes = action.payload.pdes || [];
            state.model.stores = action.payload.stores;
            state.sentStoreList = action.payload.stores;
        });
        builder.addCase(getOldCatalogueDetails.pending, (state) => {
            state.loadingStocks = true;
            state.model.catalogueStocks = [];
            state.error = "";
        });
        builder.addCase(getOldCatalogueDetails.rejected, (state, action) => {
            state.loadingStocks = false;
            state.error = action.error.message || "";
        });
    }
});

export const { addSelectedStores, addImportWizardTable, resetImportWizardState, updateCatalogueField, updateCatalogueModelField,
     cleanCatalogueDetails, addSelectedRows, addAGGridSelectedStocks, addAGGridSelectedRows, addAGGridSelectedStores} = cataloguesSlice.actions;

export default cataloguesSlice.reducer;